import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useSoundEffects } from "@/hooks/useSoundEffects";
import { SoundEffect } from "@/lib/soundEffects";

interface PageTransitionProps {
  children: React.ReactNode;
}

const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState("fadeIn");
  const [transitionType, setTransitionType] = useState<
    "slide" | "zoom" | "flip" | "cut"
  >("slide");
  const [isProjectDetail, setIsProjectDetail] = useState(false);
  const { transitionSound, playSound } = useSoundEffects();

  useEffect(() => {
    // Check if this is a project detail page
    const isDetail = location.pathname.includes("/project/");
    setIsProjectDetail(isDetail);

    // Use cut transition specifically for project detail pages
    const transitions: Array<"slide" | "zoom" | "flip" | "cut"> = isDetail
      ? ["cut"]
      : ["slide", "zoom", "flip"];
    const randomTransition =
      transitions[Math.floor(Math.random() * transitions.length)];
    setTransitionType(randomTransition);

    if (location !== displayLocation) {
      setTransitionStage("fadeOut");

      // Play transition sound when starting the transition
      // Use different sound variations based on transition type
      switch (randomTransition) {
        case "cut":
          // Sharper transition sound for cut effect
          playSound(SoundEffect.TRANSITION, 0.4);
          break;
        case "zoom":
          // Swoosh sound for zoom transitions
          playSound(SoundEffect.TRANSITION, 0.3);
          break;
        default:
          // Default transition sound
          transitionSound(0.25);
      }

      // Faster transition when going between pages
      const timeout = setTimeout(() => {
        setDisplayLocation(location);
        setTransitionStage("fadeIn");
      }, 250);

      return () => clearTimeout(timeout);
    }
  }, [location, displayLocation, transitionSound, playSound]);

  useEffect(() => {
    // Initial fade in - faster timing
    const timeout = setTimeout(() => {
      setIsVisible(true);
    }, 30);

    // Mark as loaded after animations complete
    const loadedTimeout = setTimeout(() => {
      setIsLoaded(true);

      // Subtle notification sound when fully loaded
      if (isProjectDetail) {
        // Use different sound for project detail pages
        playSound(SoundEffect.NOTIFICATION, 0.2);
      }
    }, 600); // Reduced timing for faster transitions

    return () => {
      clearTimeout(timeout);
      clearTimeout(loadedTimeout);
    };
  }, [displayLocation, isProjectDetail, playSound]);

  const getTransitionClass = () => {
    if (transitionStage === "fadeOut") {
      switch (transitionType) {
        case "slide":
          return "opacity-0 translate-y-6";
        case "zoom":
          return "opacity-0 scale-95";
        case "flip":
          return "opacity-0 rotateY-90";
        case "cut":
          return "opacity-0 clip-path-out";
        default:
          return "opacity-0";
      }
    }
    return "opacity-100";
  };

  return (
    <div
      className={cn(
        "transition-all duration-300 ease-in-out transform",
        getTransitionClass()
      )}
    >
      {/* Enhanced cutting transition effect for project detail pages */}
      {isProjectDetail && transitionStage === "fadeOut" && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          {/* Cutting animation overlay with multiple slices */}
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="absolute bg-black h-full transition-transform duration-500 ease-in-out"
              style={{
                left: `${i * 12.5}%`,
                width: "12.5%",
                transform: "translateY(-100%)",
                animation: `slice-down 600ms cubic-bezier(0.16, 1, 0.3, 1) forwards ${
                  i * 50
                }ms`,
              }}
            />
          ))}
        </div>
      )}

      {/* Standard transition overlay for non-project detail transitions */}
      {!isProjectDetail && transitionStage === "fadeOut" && (
        <div className="fixed inset-0 z-[100] pointer-events-none bg-black bg-opacity-50 transition-opacity duration-200 opacity-100"></div>
      )}

      {/* Content with staggered reveals */}
      <div
        className={cn(
          "transition-all duration-500 ease-out",
          isVisible ? "opacity-100" : "opacity-0"
        )}
      >
        {children}
      </div>

      {/* Reveal line animation after page loads - subtle page indicators */}
      {isLoaded && (
        <>
          <div className="fixed top-0 left-0 right-0 h-0.5 bg-white/20 z-40">
            <div className="h-full bg-white w-0 animate-slide-full"></div>
          </div>

          {/* Corner animations with faster timing */}
          <div
            className="fixed top-0 left-0 w-12 h-12 border-t border-l border-white/30 z-40 scale-0 animate-corner-reveal"
            style={{ transformOrigin: "top left", animationDuration: "400ms" }}
          ></div>
          <div
            className="fixed top-0 right-0 w-12 h-12 border-t border-r border-white/30 z-40 scale-0 animate-corner-reveal"
            style={{
              transformOrigin: "top right",
              animationDelay: "100ms",
              animationDuration: "400ms",
            }}
          ></div>
          <div
            className="fixed bottom-0 left-0 w-12 h-12 border-b border-l border-white/30 z-40 scale-0 animate-corner-reveal"
            style={{
              transformOrigin: "bottom left",
              animationDelay: "100ms",
              animationDuration: "400ms",
            }}
          ></div>
          <div
            className="fixed bottom-0 right-0 w-12 h-12 border-b border-r border-white/30 z-40 scale-0 animate-corner-reveal"
            style={{
              transformOrigin: "bottom right",
              animationDelay: "200ms",
              animationDuration: "400ms",
            }}
          ></div>

          {/* Additional decorative animations */}
          <div
            className="fixed top-1/4 left-0 w-4 h-4 bg-white/20 z-40 rounded-full opacity-0 animate-fade-in"
            style={{ animationDelay: "600ms", animationDuration: "800ms" }}
          ></div>
          <div
            className="fixed top-1/3 right-0 w-4 h-4 bg-white/20 z-40 rounded-full opacity-0 animate-fade-in"
            style={{ animationDelay: "700ms", animationDuration: "800ms" }}
          ></div>
          <div
            className="fixed bottom-1/4 left-0 w-4 h-4 bg-white/20 z-40 rounded-full opacity-0 animate-fade-in"
            style={{ animationDelay: "800ms", animationDuration: "800ms" }}
          ></div>
          <div
            className="fixed bottom-1/3 right-0 w-4 h-4 bg-white/20 z-40 rounded-full opacity-0 animate-fade-in"
            style={{ animationDelay: "900ms", animationDuration: "800ms" }}
          ></div>
        </>
      )}
    </div>
  );
};

export default PageTransition;
